import { Box, Typography } from '@mui/material';
import SectionLoader from 'components/shared/SectionLoader';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { formatDealAmount } from 'helper/short-currency';
import { MouseEvent, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import { DealsService } from 'service/deals.service';
import DealCard from '../DealCard';

import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { filtersToQueryParams } from '../../utils';
import { DealsSettings } from '../DealsSettings';
import { muiStyles } from './muiStyles';
import NoDeals from './NoDeals';
import StageChangeModal from './StageChangeModal.tsx';
import { MultiPipelineKanbanProps } from './types';
import { calculateColumnEarnings } from './utils';

const MultiPipelineKanban = ({
  filterState,
  tabValue,
  searchTerm = '',
  needsAttention = false,
  mineOverride,
  clientNameOverride,
  subSections = false,
}: MultiPipelineKanbanProps) => {
  const [deals, setDeals] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [dealCardElement, setDealCardElement] = useState<SVGSVGElement | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    stageChange: false,
  });
  const { alertState, setAlertState, getAlertContent, completeOperation, startOperation } =
    useAlertStatus();

  const getDeals = useCallback(async () => {
    try {
      setLoading(true);

      const queryParams = filtersToQueryParams(
        filterState,
        tabValue,
        searchTerm,
        needsAttention,
        undefined,
        mineOverride,
        clientNameOverride,
      );
      const response = await DealsService.getKanban(queryParams);

      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  }, [clientNameOverride, filterState, mineOverride, needsAttention, searchTerm, tabValue]);

  useEffect(() => {
    getDeals();
  }, [filterState, tabValue, searchTerm, needsAttention, getDeals]);

  const handleOpenSettings = (event: MouseEvent<SVGSVGElement>, deal: any) => {
    event.stopPropagation();
    setSelectedDeal(deal);
    setDealCardElement(event.currentTarget);
  };

  const handleStageChange = () => {
    getDeals();
  };

  function handleOnDrag(e: React.DragEvent, name: string, sourceStage: string) {
    e.dataTransfer?.setData('name', name);
    e.dataTransfer?.setData('sourceStage', sourceStage);
  }

  function handleOnDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  // extract
  enum combinedStageColumns {
    'Exploring/Identified',
    'Qualified/Pitching',
  }

  const map: Record<keyof typeof combinedStageColumns, string[]> = {
    'Exploring/Identified': ['Exploring', 'Identified'],
    'Qualified/Pitching': ['Qualified', 'Pitching'],
  };

  const [pendingDrop, setPendingDrop] = useState<{
    dealId: string;
    sourceStage: string;
    deals: any;
  } | null>(null);

  const handledSelectStage = async (cancel: boolean, chosenStage = '') => {
    modalDispatch({ type: 'CLOSE', payload: 'stageChange' });

    if (cancel || !chosenStage || !pendingDrop) {
      setPendingDrop(null);
      return;
    }

    await submitStageChange(chosenStage, pendingDrop.dealId, pendingDrop.deals);
    setPendingDrop(null);
  };
  const modalStagesArray = useRef(map['Exploring/Identified']);

  async function handleOnDrop(e: React.DragEvent, target: string) {
    const name = e.dataTransfer.getData('name');
    const sourceStage = e.dataTransfer.getData('sourceStage');
    let targetStage = target;

    if (sourceStage === target) return;

    if (!deals) return;

    const stageDeals = deals[sourceStage].deals;
    const dealToMove = stageDeals.find((d: any) => d.name === name);
    if (!dealToMove) return;

    const dealId = dealToMove.dealId;
    if (!dealId) {
      completeOperation('update', 'Deal ID not found', true);
      return;
    }

    if (targetStage in combinedStageColumns) {
      modalStagesArray.current = map[targetStage as keyof typeof combinedStageColumns];
      setPendingDrop({ dealId, sourceStage, deals });
      modalDispatch({ type: 'OPEN', payload: 'stageChange' });
      return;
    }

    await submitStageChange(targetStage, dealId, deals);
  }

  const submitStageChange = async (targetStage: string, dealId: string, prevDeals: any) => {
    try {
      startOperation('update', 'Updating deal stage...');

      const payload = { stageName: targetStage };
      await DealsService.updateDeal(dealId, payload);
      await getDeals();

      completeOperation('update', 'Deal stage updated successfully');
    } catch (error: any) {
      console.error('Error updating deal:', error);
      setDeals(prevDeals);
      completeOperation(
        'update',
        error?.response?.data?.message || 'Failed to update deal stage',
        true,
      );
    }
  };

  const handleCloseStageChangeModal = () => {
    modalDispatch({ type: 'CLOSE', payload: 'stageChange' });
  };

  if (loading) return <SectionLoader />;
  if (!deals) return <NoDeals />;

  return (
    <Box sx={muiStyles.root}>
      {Object.keys(deals).map((stage: any) => {
        const { dealsCount: count, deals: data } = deals[stage];
        const earnings = formatDealAmount(calculateColumnEarnings(data));

        if (stage === 'Completed') return null;

        return (
          <Box
            key={stage}
            sx={muiStyles.stage}
            onDragOver={handleOnDragOver}
            onDrop={(e) => handleOnDrop(e, stage)}
          >
            <Box sx={muiStyles.stageHeader}>
              <Typography variant='responsiveHeading4'>
                {stage} ({count})
              </Typography>
              <Typography variant='responsiveHeading4' sx={muiStyles.stageAmount}>
                {earnings}
              </Typography>
            </Box>
            <Box
              id={`kanban-column-${stage}`}
              sx={{
                ...muiStyles.stageColumn,
                ...(subSections && {
                  height: { xs: 'calc(100vh - 500px)', xl: 'calc(100vh - 550px)' },
                }),
              }}
            >
              {data.map((deal: any) => (
                <DealCard
                  key={deal.dealId}
                  data={deal}
                  onDrag={(e) => handleOnDrag(e, deal.name, stage)}
                  onSettingsClick={handleOpenSettings}
                />
              ))}
            </Box>
          </Box>
        );
      })}

      <DealsSettings
        anchorElement={dealCardElement}
        onClose={() => {
          setDealCardElement(null);
          setSelectedDeal(null);
        }}
        startOperation={startOperation}
        completeOperation={completeOperation}
        deal={selectedDeal}
        onDealDeleted={getDeals}
        onStageChange={handleStageChange}
      />

      <StageChangeModal
        isOpen={modalState.stageChange}
        handleClose={handleCloseStageChangeModal}
        handledSelectStage={handledSelectStage}
        stages={modalStagesArray.current}
      />

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default MultiPipelineKanban;
